/**
 * CTRL_11 Plugin - Incident Response Readiness
 * Custom UI with incident tracking, response time metrics, and playbook status
 * Extends GenericConfigPanel with incident dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Tag,
  Space,
  Timeline,
  Progress,
} from 'antd';
import {
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Incident Response Dashboard Component
 */
function IncidentResponseDashboard({ config, projectId }) {
  const [stats, setStats] = useState({
    activeIncidents: 0,
    resolvedIncidents: 0,
    avgResponseTime: 0,
    slaCompliance: 0,
  });
  const [recentIncidents, setRecentIncidents] = useState([]);

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchIncidentData();
    }
  }, [config, projectId]);

  const fetchIncidentData = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setStats({
        activeIncidents: 2,
        resolvedIncidents: 48,
        avgResponseTime: 3.2,
        slaCompliance: 96,
      });
      setRecentIncidents([
        {
          id: 1,
          type: 'Data Breach',
          severity: 'critical',
          status: 'investigating',
          reported: new Date(Date.now() - 1800000).toISOString(),
          responseTime: 0.5,
        },
        {
          id: 2,
          type: 'Model Drift',
          severity: 'high',
          status: 'mitigating',
          reported: new Date(Date.now() - 7200000).toISOString(),
          responseTime: 1.2,
        },
        {
          id: 3,
          type: 'API Failure',
          severity: 'medium',
          status: 'resolved',
          reported: new Date(Date.now() - 86400000).toISOString(),
          responseTime: 2.5,
        },
      ]);
    }, 500);
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity) => {
        const colorMap = {
          critical: 'red',
          high: 'orange',
          medium: 'gold',
          low: 'blue',
        };
        return <Tag color={colorMap[severity]}>{severity.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          investigating: 'orange',
          mitigating: 'blue',
          resolved: 'green',
        };
        return <Tag color={colorMap[status]}>{status}</Tag>;
      },
    },
    {
      title: 'Response Time (hrs)',
      dataIndex: 'responseTime',
      key: 'responseTime',
      align: 'right',
    },
    {
      title: 'Reported',
      dataIndex: 'reported',
      key: 'reported',
      render: (ts) => new Date(ts).toLocaleString(),
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Incident Response Disabled"
        description="Enable to track and manage security incidents and response workflows."
        type="info"
        showIcon
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Incident Statistics */}
      <Row gutter={16}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Active Incidents"
              value={stats.activeIncidents}
              prefix={<AlertOutlined />}
              valueStyle={{ color: stats.activeIncidents > 0 ? colors.status.fail : colors.status.pass }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Resolved (30d)"
              value={stats.resolvedIncidents}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Avg Response Time"
              value={stats.avgResponseTime}
              suffix="hrs"
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="SLA Compliance"
              value={stats.slaCompliance}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: stats.slaCompliance >= 95 ? colors.status.pass : colors.status.warning }}
            />
            <Progress
              percent={stats.slaCompliance}
              strokeColor={stats.slaCompliance >= 95 ? colors.status.pass : colors.status.warning}
              size="small"
              showInfo={false}
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Recent Incidents Table */}
      <div>
        <h4>Recent Incidents</h4>
        <Table
          columns={columns}
          dataSource={recentIncidents}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </div>

      {stats.activeIncidents > 0 && (
        <Alert
          message={`${stats.activeIncidents} active incident(s) require attention`}
          type="error"
          showIcon
          icon={<WarningOutlined />}
        />
      )}
    </Space>
  );
}

/**
 * CTRL_11 Plugin Component
 */
function CTRL_11_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    response_sla_hours: 24,
  });

  return (
    <div>
      {/* Incident Dashboard */}
      <IncidentResponseDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_11_Plugin;
